import type { IBatchStore } from '../../batch/batch-store-types';
import logger from '../../../utils/logger';

/**
 * Finds existing substitute material in the scale data
 */
export const findExistingSubstituteMaterial = (
  runningData: any,
  scaleId: number | string,
  substituteMaterialId: string | number,
): number => {
  return runningData.scales[scaleId].findIndex((weighedData: any) =>
    String(weighedData.material.itemId) === String(substituteMaterialId),
  );
};

/**
 * Combines weights when substitute material already exists
 */
export const combineSubstituteWeights = (
  existingData: any,
  currentWeight: number,
  newWeighedData: any,
): void => {
  logger.log('%c=== COMBINE SUBSTITUTE WEIGHTS DEBUG ===', 'color: #ff00ff; font-weight: bold');
  logger.log('Existing data originalMaterial:', existingData.originalMaterial);
  logger.log('New weighed data originalMaterial:', newWeighedData.originalMaterial);
  logger.log('Full existing data:', existingData);
  logger.log('Full new weighed data:', newWeighedData);

  const remainder = Math.max(0, newWeighedData.mft || 0);
  const isProportionBatched = existingData.proportionOneTarget > 0;

  if (existingData.isFeedFinish) {
    // Retain completed targets for later cumulative feeds, but do not feed them again.
    // A proportion-batched entry's mft only holds its last phase, so take the whole schedule.
    const completedTarget = isProportionBatched
      ? existingData.proportionOneTarget + (existingData.proportionTwoTarget || 0)
      : existingData.mft;
    existingData.previouslyFedTarget = (existingData.previouslyFedTarget || 0) + completedTarget;
    existingData.mft = 0;
    existingData.unconvertedTarget = 0;

    if (isProportionBatched) {
      // The schedule is done; the reopened entry feeds only the transferred remainder, in one go.
      existingData.proportionOneTarget = 0;
      existingData.proportionTwoTarget = 0;
      existingData.proportionOneActual = 0;
      existingData.isFeedFinishProportionOne = false;
      existingData.isFeedFinishProportionTwo = false;
    }
  } else if (isProportionBatched) {
    // batchMaterial derives the feed target from the proportion schedule, so the remainder has to go into
    // the phase that feeds next: proportion two once proportion one is done, otherwise proportion one.
    if (existingData.isFeedFinishProportionOne) {
      existingData.proportionTwoTarget = (existingData.proportionTwoTarget || 0) + remainder;
    } else {
      existingData.proportionOneTarget += remainder;
    }
  }

  // Add current weight to existing substitute material's weight tracking
  if (typeof existingData.substitutionWeight === 'number') {
    existingData.substitutionWeight += currentWeight;
  } else {
    existingData.substitutionWeight = currentWeight;
  }

  // Update target weight if it needs to be increased
  if (newWeighedData.mft && existingData.mft) {
    existingData.mft += newWeighedData.mft;
  } else if (newWeighedData.mft) {
    existingData.mft = newWeighedData.mft;
  }

  // Update unconverted target if present
  if (newWeighedData.unconvertedTarget && existingData.unconvertedTarget) {
    existingData.unconvertedTarget += newWeighedData.unconvertedTarget;
  } else if (newWeighedData.unconvertedTarget) {
    existingData.unconvertedTarget = newWeighedData.unconvertedTarget;
  }

  // Preserve originalMaterial if it doesn't exist in existing data
  if (!existingData.originalMaterial && newWeighedData.originalMaterial) {
    existingData.originalMaterial = newWeighedData.originalMaterial;
  }

  logger.log('After combine - existing data originalMaterial:', existingData.originalMaterial);
  logger.log('%c=== END COMBINE SUBSTITUTE WEIGHTS ===', 'color: #ff00ff; font-weight: bold');
};

/**
 * Updates the existing substitute material entry with new bin and flags
 */
export const updateExistingSubstituteEntry = (
  existingData: any,
  substituteBin: any,
  originalScaleData: any,
): void => {
  logger.log('%c=== UPDATE EXISTING SUBSTITUTE ENTRY DEBUG ===', 'color: #00ff00; font-weight: bold');
  logger.log('Existing data before update:', existingData);
  logger.log('Original scale data:', originalScaleData);
  logger.log('Existing data has originalMaterial?', !!existingData.originalMaterial);
  logger.log('Original scale data has material?', !!originalScaleData.material);

  // Mark that this entry now includes substituted material
  existingData.isSubstituted = true;

  // Reset feed finish status if this was a previously finished material being reused
  if (existingData.isFeedFinish) {
    existingData.isFeedFinish = false;
  }

  // Update bin information to use the substitute bin
  existingData.bin = substituteBin;
  existingData.binPercent = {};
  existingData.jogSecondary = false;
  if (existingData.constituent) {
    existingData.constituent = { ...existingData.constituent, binPercent: {} };
  }

  // Ensure originalMaterial is set from the original scale data if not already present
  if (!existingData.originalMaterial && originalScaleData.material) {
    logger.log('%cSetting originalMaterial from originalScaleData:', 'color: #ffff00', originalScaleData.material);
    existingData.originalMaterial = originalScaleData.material;
  }

  // Ensure originalBin is set from the original scale data if not already present
  if (!existingData.originalBin && originalScaleData.bin) {
    existingData.originalBin = originalScaleData.bin;
  }

  logger.log('Existing data after update:', existingData);
  logger.log('%c=== END UPDATE EXISTING SUBSTITUTE ENTRY ===', 'color: #00ff00; font-weight: bold');

  // Keep the original material's data but mark it as finished and interrupted
  originalScaleData.isFeedFinish = true;
  originalScaleData.isFeedInterrupted = true;
};

/**
 * Logs the combination of substitute materials
 */
export const logCombinedSubstitution = (
  batchStore: IBatchStore,
  substituteMaterial: any,
  currentWeight: number,
  existingData: any,
  scaleId: number | string,
): void => {
  batchStore.saveLog({
    level: 'info',
    message: `Combined substitute material weight: ${substituteMaterial.code} total weight increased by ${currentWeight}`,
    data: {
      existingWeight: existingData.substitutionWeight - currentWeight,
      addedWeight: currentWeight,
      newTotalWeight: existingData.substitutionWeight,
      materialId: substituteMaterial.itemId,
      scaleId: scaleId,
      combined: true,
    },
  });
};

/**
 * Handles the creation of a new substitute material entry
 */
export const createNewSubstituteEntry = (
  runningData: any,
  scaleId: number | string,
  scaleDataIndex: number,
  newWeighedData: any,
): any => {
  runningData.scales[scaleId].splice(scaleDataIndex + 1, 0, newWeighedData);
  return newWeighedData;
};

/**
 * Logs the substitution completion based on whether it was combined or new
 */
export const logSubstitutionCompletion = (
  batchStore: IBatchStore,
  isCombined: boolean,
  originalMaterial: any,
  substituteMaterial: any,
  scaleId: number | string,
  activeSubstituteScaleData?: any,
): void => {
  if (isCombined) {
    batchStore.saveLog({
      level: 'info',
      message: 'Material substitution completed by combining with existing substitute material entry',
      data: {
        action: 'combined_substitution',
        originalMaterial: originalMaterial.code,
        substituteMaterial: substituteMaterial.code,
        combinedWeight: activeSubstituteScaleData.substitutionWeight,
        scaleId: scaleId,
      },
    });
  } else {
    batchStore.saveLog({
      level: 'info',
      message: 'Material substitution completed by creating new substitute material entry',
      data: {
        action: 'new_substitution',
        originalMaterial: originalMaterial.code,
        substituteMaterial: substituteMaterial.code,
        scaleId: scaleId,
      },
    });
  }
};
